/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Dropdown,
  Form,
  Grid,
  Input,
  Menu,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  DownOutlined,
  EyeOutlined,
  FileExcelOutlined,
  MailOutlined,
  PrinterOutlined,
  SearchOutlined,
  SendOutlined,
} from "@ant-design/icons";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import * as dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_DD_MM_YYYY_HH_mm, DATE_FORMAT_YYYY_MM_DD } from "constants/DateConstant";
import utils from "utils";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { isLoaded, useFirestore, useFirestoreConnect } from "react-redux-firebase";
import { CURRENCY, DELIVERY_OPTIONS } from "constants/DeliveryConstants";
import { FIRESTORE_EMAIL_QUEUE_TABLE, FIRESTORE_ORDERS_TABLE } from "../../../../constants/FirestoreConstant";
import {
  ORDER_STATUS_COMPLETED,
  ORDER_STATUS_DELIVERED,
  ORDER_STATUS_IN_TRANSIT,
  ORDER_STATUS_PICKED_UP,
  ORDER_STATUS_READY_FOR_SHIPPING,
} from "../../../../constants/OrderConstant";
import { getOrdersExcelExportData } from "functions/excel";
import { useReactToPrint } from "react-to-print";
import { getBarcodeData } from "../../../../functions/orders";
import { formatPrice, getPaymentStatus, getPaymentStatusColor } from "../../../../functions/formatters";
import _ from "lodash";
import locale from "antd/es/date-picker/locale/nl_NL";
import { COMPANY_CONSTANTS } from "../../../../constants/CompanyConstants";
import { setOrdersMaxDaysAgo } from "../../../../redux/actions/Queries";
import { sendOrderToExact } from "../../../../functions/api/exact";
import { resend_confirmation_email } from "../../../../urls";
import axios from "axios";
import { getFirebaseIDToken } from "../../../../functions/firebase/firebase";
import { createShipmentFromOrder, getShipmentErrorMessage } from "../../../../functions/api/svz";
import Barcode from "../../../../components/shared-components/Table/Barcode";
import isBetween from "dayjs/plugin/isBetween";
import ExcelExport from "../../../../components/shared-components/ExcelExport";
import OrderDrawer from "../../../../components/shared-components/Orders/OrderDrawer";
import styled from "styled-components";

dayjs.extend(isBetween);

const { Option } = Select;
const { RangePicker } = DatePicker;

const Orders = () => {
  const firestore = useFirestore();
  const dispatch = useDispatch();
  const barcodeRef = useRef();
  useFirestoreConnect([{ collection: FIRESTORE_EMAIL_QUEUE_TABLE, orderBy: ["scheduledFor", "desc"] }]);
  const auth = useSelector((state) => state.firebaseReducer.auth);
  const orders = useSelector((state) => state.firestoreReducer.ordered.orders);
  const emailQueue = useSelector((state) => state.firestoreReducer.ordered.emailQueue);
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const orderMaxDaysAgoQuery = useSelector((state) => state.queries.ordersMaxDaysAgo);
  const [messageApi, contextHolder] = message.useMessage();
  const [filteredList, setFilteredList] = useState(orders);
  const [paymentStatusList, setPaymentStatusList] = useState([]);
  const [orderStatusList, setOrderStatusList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [visible, setVisible] = useState(false);
  const [order, setOrder] = useState(null);
  const { t } = useTranslation();
  const [datePickerForm] = Form.useForm();
  const [dateRange, setDateRange] = useState([]);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState("All");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("All");
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [findingById, setFindingById] = useState(false);
  const [creatingShipment, setCreatingShipment] = useState({});
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [displayCount, setDisplayCount] = useState(20);

  useEffect(() => {
    if (!!orders) {
      // Map incoming orders to include a backward-compatible orderStatus field derived from the new structure
      const transformed = orders.map((o) => ({
        ...o,
        // Backward-compatible projection for UI that still references order.orderStatus
        orderStatus: o?.status?.current ?? o?.orderStatus ?? "CREATED",
        // Convenience projections for potential usage
        _paymentMethod: o?.payment?.method ?? o?.paymentMethod,
        _paymentStatus: o?.payment?.status?.current ?? o?.paymentStatus,
        _paidAt: o?.payment?.timestamps?.paidAt ?? o?.paidAt,
        _paymentUpdatedAt: o?.payment?.timestamps?.updatedAt ?? o?.updatedAt,
      }));

      // Filter out CREATED by default (based on derived orderStatus)
      let filteredOrders = transformed.filter((order) => order.orderStatus !== "CREATED");
      setFilteredList(filteredOrders);

      // Build list of unique ORDER statuses from derived field
      const uniqueStatuses = _.uniq(transformed.map((o) => o.orderStatus).filter((s) => !!s));
      setPaymentStatusList(uniqueStatuses);

      // Keep available transitions list as-is for now if needed elsewhere
      setOrderStatusList([
        ORDER_STATUS_READY_FOR_SHIPPING,
        ORDER_STATUS_PICKED_UP,
        ORDER_STATUS_IN_TRANSIT,
        ORDER_STATUS_DELIVERED,
        ORDER_STATUS_COMPLETED,
      ]);
    }
  }, [orders]);

  const handleShowStatus = (value) => {
    setSelectedOrderStatus(value);
    setSearchText("");
    setDisplayCount(20);
    applyFilters({
      orderStatus: value,
      paymentMethod: selectedPaymentMethod,
      paymentStatus: selectedPaymentStatus,
      search: searchText,
    });
  };

  const handlePaymentMethodFilter = (value) => {
    setSelectedPaymentMethod(value);
    setDisplayCount(20);
    applyFilters({
      orderStatus: selectedOrderStatus,
      paymentMethod: value,
      paymentStatus: selectedPaymentStatus,
      search: searchText,
    });
  };

  const handlePaymentStatusFilter = (value) => {
    setSelectedPaymentStatus(value);
    setDisplayCount(20);
    applyFilters({
      orderStatus: selectedOrderStatus,
      paymentMethod: selectedPaymentMethod,
      paymentStatus: value,
      search: searchText,
    });
  };

  const showDrawer = (row) => {
    setVisible(true);
    // Get corresponding email queue items
    const emailQueueItems = emailQueue?.filter((item) => item.orderId === row.id);
    const fullOrder = { ...row, emailQueue: emailQueueItems };
    setOrder(fullOrder);
  };

  const closeDrawer = () => {
    setVisible(false);
  };

  const formatShippingMethod = (value) => {
    if (value === DELIVERY_OPTIONS.SEND) return t("shipping");
    if (value === DELIVERY_OPTIONS.PICKUP) return t("pickup");
  };

  const handleManualPrint = (functionName) => {
    setIsPrinting(true);
    setTimeout(() => {
      functionName();
    }, 1000);
  };

  const handleBarcodesPrint = useReactToPrint({
    contentRef: barcodeRef,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const updateFirebaseOrderStatus = (order, status) => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
    // Build new status structure according to mutation rules
    const now = Math.floor(Date.now() / 1000);
    const prevCurrent = order?.status?.current ?? order?.orderStatus ?? null;
    const prevHistory = order?.status?.history ?? [];
    const nextStatus = {
      current: status,
      history: [
        ...prevHistory,
        {
          from: prevCurrent,
          to: status,
          at: now,
          source: "cms",
          user: auth.uid,
        },
      ],
    };

    firestore
      .collection(FIRESTORE_ORDERS_TABLE)
      .doc(order.id)
      .update({ status: nextStatus })
      .then(() => messageApi.success(t("order.status_changed_success")))
      .catch(() => messageApi.error(t("order.status_changed_failure")));
  };

  const updateFirebasePaymentStatus = (order, nextPaymentStatus) => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
    const now = Math.floor(Date.now() / 1000);
    const payment = order?.payment || {};
    const prevCurrent = payment?.status?.current ?? null;
    const prevHistory = payment?.status?.history ?? [];
    const method = payment?.method || order?.paymentMethod || "";

    const next = {
      method,
      status: {
        current: nextPaymentStatus,
        history: [...prevHistory, { from: prevCurrent, to: nextPaymentStatus, at: now, source: "cms" }],
      },
      timestamps: {
        ...(payment?.timestamps || {}),
        updatedAt: now,
        ...(nextPaymentStatus === "paid" ? { paidAt: now } : {}),
      },
    };

    firestore
      .collection(FIRESTORE_ORDERS_TABLE)
      .doc(order.id)
      .update({ payment: next })
      .then(() => messageApi.success(t("order.payment_status_changed_success")))
      .catch((err) => {
        console.log(err);
        messageApi.error(t("order.payment_status_changed_failure"));
      });
  };

  const getFilteredOrders = (_unused) => {
    let filtered;
    if (!!dateRange && dateRange.length > 0) {
      filtered = filterOrdersDateRange(orders, dateRange);
    } else {
      filtered = filterOrdersRows(selectedRows);
    }
    // Order by date ascending
    return _.orderBy(filtered, ["createdAt"], ["asc"]);
  };

  const filterOrdersDateRange = (ordersList, drange) => {
    const startDate = drange[0].format(DATE_FORMAT_YYYY_MM_DD);
    const endDate = drange[1].format(DATE_FORMAT_YYYY_MM_DD);
    return _.filter(ordersList || [], function (order) {
      return dayjs(order?.delivery?.date, DATE_FORMAT_DD_MM_YYYY).isBetween(startDate, endDate, undefined, "[]");
    }).map((o) => ({ ...o, orderStatus: o?.status?.current ?? o?.orderStatus ?? "CREATED" }));
  };

  const filterOrdersRows = (rows) => {
    return (rows || []).map((o) => ({
      ...o,
      orderStatus: o?.status?.current ?? o?.orderStatus ?? "CREATED",
      _paymentMethod: o?.payment?.method ?? o?.paymentMethod,
      _paymentStatus: o?.payment?.status?.current ?? o?.paymentStatus,
    }));
  };

  const PAYMENT_METHODS = ["invoice", "stripe"];
  const PAYMENT_STATUSES_BY_METHOD = {
    invoice: ["not_sent", "sent", "paid", "overdue"],
    stripe: ["not_paid", "paid", "refunded"],
  };

  const applyFilters = ({ orderStatus, paymentMethod, paymentStatus, search }) => {
    const list = (orders || []).map((o) => ({
      ...o,
      orderStatus: o?.status?.current ?? o?.orderStatus ?? "CREATED",
      _paymentMethod: o?.payment?.method ?? o?.paymentMethod,
      _paymentStatus: o?.payment?.status?.current ?? o?.paymentStatus,
    }));

    let out = list;
    if (orderStatus && orderStatus !== "All") {
      out = out.filter((o) => o.orderStatus === orderStatus);
    }
    if (paymentMethod && paymentMethod !== "All") {
      out = out.filter((o) => (o._paymentMethod || "").toLowerCase() === paymentMethod);
    }
    if (paymentStatus && paymentStatus !== "All") {
      out = out.filter((o) => (o._paymentStatus || "").toLowerCase() === paymentStatus);
    }
    if (search && search.trim()) {
      out = utils.wildCardSearchOnOrder(out, search.trim(), orderStatus);
    }
    setFilteredList(out);
  };

  const OrderCard = ({ order }) => {
    return (
      <MobileOrderCard onClick={() => showDrawer(order)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: 0, marginBottom: 4, fontSize: 16, fontWeight: 600 }}>{order.orderId}</h4>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>
              {order.customer?.company || order.customer?.firstName + " " + order.customer?.lastName}
            </div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {dayjs.unix(order.createdAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <Tag color={getPaymentStatusColor(order.orderStatus)}>{getPaymentStatus(order.orderStatus)}</Tag>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#52c41a" }}>
              {CURRENCY}
              {formatPrice(order.totalPrice)}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: "#999" }}>
          {order.delivery?.date && (
            <div>
              {formatShippingMethod(order.delivery?.method)}: {order.delivery?.date}
            </div>
          )}
        </div>
      </MobileOrderCard>
    );
  };

  const orderStatusMenu = (order) => (
    <Menu>
      {orderStatusList
        .filter((item) => item !== order.orderStatus)
        .map((item, key) => {
          return (
            <Menu.Item key={key}>
              <Popconfirm
                title={
                  <p>
                    {t("order.change_status.intro", { orderId: order.orderId })}{" "}
                    <b>
                      {t("order.change_status.bold", {
                        status: getPaymentStatus(item)?.toLowerCase(),
                      })}
                    </b>
                    {t("order.change_status.end")}
                  </p>
                }
                onConfirm={() => updateFirebaseOrderStatus(order, item)}
                okText={t("yes")}
                cancelText={t("no")}
              >
                <a onClick={(e) => e.preventDefault()}>{getPaymentStatus(item)}</a>
              </Popconfirm>
            </Menu.Item>
          );
        })}
    </Menu>
  );

  const exactStatusMenu = (order) => (
    <Menu>
      <Menu.Item>
        <a onClick={(e) => handleSendToExact(e, order)}>{t("send_to_exact")}</a>
      </Menu.Item>
    </Menu>
  );

  const resendConfirmationMail = (order) => {
    axios
      .post(resend_confirmation_email, { order: order, customerEmail: order.customer.email })
      .then((res) => {
        messageApi.success(t("notifications.resend_confirmation_mail_success"));
      })
      .catch((err) => messageApi.error(err.message));
  };

  const handleCreateShipment = async (order) => {
    setCreatingShipment({ ...creatingShipment, [order.id]: true });
    try {
      await createShipmentFromOrder(order);
      messageApi.success(t("shipment.created_success", { orderId: order.orderId }));
    } catch (error) {
      messageApi.error(getShipmentErrorMessage(error));
    } finally {
      setCreatingShipment({ ...creatingShipment, [order.id]: false });
    }
  };

  const dropdownMenu = (row) => {
    const canCreateShipment = ["RECEIVED"].includes(row?.status?.current);
    const hasShipment = row.shipmentIds && row.shipmentIds.length > 0;

    return (
      <Menu>
        <Menu.Item>
          <a onClick={() => showDrawer(row)}>
            <Flex alignItems="center">
              <EyeOutlined />
              <span style={{ marginLeft: 4 }}>{t("details")}</span>
            </Flex>
          </a>
        </Menu.Item>
        <Menu.Item>
          <Flex alignItems="center">
            <MailOutlined />
            <Popconfirm
              placement="bottom"
              title={t("confirm_resend_confirmation_mail", {
                customer: row.customer.company,
              })}
              onConfirm={() => resendConfirmationMail(row)}
              okText={t("yes")}
              cancelText={t("no")}
            >
              <span style={{ marginLeft: 4 }}>{t("resend_confirmation_mail")}</span>
            </Popconfirm>
          </Flex>
        </Menu.Item>
        {canCreateShipment && (
          <Menu.Item disabled={hasShipment || creatingShipment[row.id]}>
            <Flex alignItems="center">
              <SendOutlined />
              <Popconfirm
                placement="bottom"
                title={t("shipment.create_confirm", { orderId: row.orderId })}
                onConfirm={() => handleCreateShipment(row)}
                okText={t("yes")}
                cancelText={t("no")}
                disabled={hasShipment || creatingShipment[row.id]}
              >
                <span style={{ marginLeft: 4 }}>
                  {hasShipment
                    ? t("shipment.already_exists")
                    : creatingShipment[row.id]
                      ? t("shipment.creating")
                      : t("shipment.create")}
                </span>
              </Popconfirm>
            </Flex>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const exportMenu = (
    <Menu>
      <Menu.Item key="1">
        <ExcelExport
          data={
            selectedRowKeys.length > 0 || (!!dateRange && dateRange.length > 0)
              ? getOrdersExcelExportData(getFilteredOrders(null))
              : getOrdersExcelExportData([])
          }
          fileName={COMPANY_CONSTANTS.WEBSITE_NAME + " bestellingen " + dayjs().format(DATE_FORMAT_DD_MM_YYYY)}
        >
          {t("excel")}
        </ExcelExport>
      </Menu.Item>
    </Menu>
  );

  const handleSendToExact = (e, order) => {
    e.preventDefault();
    getFirebaseIDToken().then((token) => {
      sendOrderToExact(order, token)
        .then(() => messageApi.success(t("notifications.order_saved_to_exact")))
        .catch(() => messageApi.error(t("notifications.order_not_saved_to_exact")));
    });
  };

  const printMenu = (
    <Menu>
      {/*<Menu.Item key="1" onClick={handleOrderListPrint}>*/}
      {/*  {t("order.orders")}*/}
      {/*</Menu.Item>*/}
      {/*<Menu.Item key="2" onClick={handlePickListPrint}>*/}
      {/*  {t("order.pick_list")}*/}
      {/*</Menu.Item>*/}
      {/*<Menu.Item key="3" onClick={handlePackingSlipPrint}>*/}
      {/*  {t("order.packing_slips")}*/}
      {/*</Menu.Item>*/}
      {/*<Menu.Item key="4" onClick={handleSendListPrint}>*/}
      {/*  {t("order.send_labels")}*/}
      {/*</Menu.Item>{" "}*/}
      <Menu.Item key="5" onClick={() => handleManualPrint(handleBarcodesPrint)}>
        {t("order.barcodes")}
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: t("order.order_id"),
      dataIndex: "orderId",
      render: (_, order) => (
        <a
          onClick={(e) => {
            e.preventDefault();
            showDrawer(order);
          }}
        >
          {order.orderId}
        </a>
      ),
    },
    {
      title: t("company"),
      dataIndex: "customer",
      render: (data) => <span>{data.company}</span>,
    },
    {
      title: t("order_date"),
      dataIndex: "createdAt",
      render: (data) => <span>{dayjs.unix(data).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "createdAt"),
      defaultSortOrder: "descend",
    },
    {
      title: t("preferred_delivery_date"),
      dataIndex: "delivery",
      render: (data) => (
        <span>
          {!!data && !!data.preferredDateTimestamp
            ? dayjs.unix(data.preferredDateTimestamp).format("dddd D MMMM")
            : "-"}
        </span>
      ),
      sorter: (a, b) => a.delivery.preferredDateTimestamp - b.delivery.preferredDateTimestamp,
    },
    {
      title: t("order.status"),
      dataIndex: "status",
      render: (_, order) => (
        <Dropdown overlay={orderStatusMenu(order)} trigger={["click"]}>
          <a onClick={(e) => e.preventDefault()}>
            <Tag color={getPaymentStatusColor(order?.status?.current ?? order.orderStatus)}>
              {getPaymentStatus(order?.status?.current ?? order.orderStatus)}
            </Tag>
          </a>
        </Dropdown>
      ),
      sorter: (a, b) =>
        (a?.status?.current ?? a?.orderStatus ?? "").localeCompare(b?.status?.current ?? b?.orderStatus ?? ""),
    },
    {
      title: t("payment_status"),
      dataIndex: "payment",
      render: (_, order) => {
        const method = (order?.payment?.method || order?._paymentMethod || "").toLowerCase();
        const current = (order?.payment?.status?.current || order?._paymentStatus || "").toLowerCase();
        const menu = (
          <Menu>
            {(PAYMENT_STATUSES_BY_METHOD[method] || [])
              .filter((s) => s !== current)
              .map((s) => (
                <Menu.Item key={s}>
                  <Popconfirm
                    title={
                      <p>
                        {t("order.change_payment_status.intro", { orderId: order.orderId })} <b>{t(s).toLowerCase()}</b>
                        ?
                      </p>
                    }
                    onConfirm={() => updateFirebasePaymentStatus(order, s)}
                    okText={t("yes")}
                    cancelText={t("no")}
                  >
                    <a onClick={(e) => e.preventDefault()}>{t(s)}</a>
                  </Popconfirm>
                </Menu.Item>
              ))}
          </Menu>
        );
        return (
          <Space>
            <Dropdown overlay={menu} trigger={["click"]}>
              <a onClick={(e) => e.preventDefault()}>
                <Tag color={getPaymentStatusColor(current)}>{t(current) || "-"}</Tag>
              </a>
            </Dropdown>
          </Space>
        );
      },
      sorter: (a, b) =>
        (a?.payment?.status?.current || a?._paymentStatus || "").localeCompare(
          b?.payment?.status?.current || b?._paymentStatus || "",
        ),
    },
    {
      title: t("saved_to_exact"),
      dataIndex: "savedToExact",
      render: (_, order) =>
        order.exactGuid || order.exactGuids?.length > 0 ? (
          <Tooltip
            title={
              <p style={{ whiteSpace: "pre-line", marginBottom: 0 }}>
                {order.exactGuids?.length > 0 ? order.exactGuids.join("\n\n") : order.exactGuid}
              </p>
            }
          >
            <span>{t("yes")}</span>
          </Tooltip>
        ) : (
          <Dropdown overlay={exactStatusMenu(order)} trigger={["click"]}>
            <a onClick={(e) => e.preventDefault()}>
              <span>{t("no")}</span>
            </a>
          </Dropdown>
        ),
    },
    {
      title: t("total"),
      dataIndex: "totalPrice",
      render: (_, order) =>
        !!order.vat ? (
          <div>
            <span className="font-weight-semibold">{CURRENCY + formatPrice(order.totalPrice)}</span>
            <span> - ({CURRENCY + formatPrice(order.vat + order.totalPrice)})</span>
          </div>
        ) : (
          <span className="font-weight-semibold">{CURRENCY + formatPrice(order.totalPrice)}</span>
        ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalPrice"),
    },
    {
      title: "",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  const rowSelection = {
    onChange: (key, rows) => {
      setSelectedRows(rows);
      setSelectedRowKeys(key);
      if (!!dateRange && dateRange.length > 0) {
        setDateRange([]);
        datePickerForm.resetFields();
      }
    },
  };

  const onSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setDisplayCount(20);
    applyFilters({
      orderStatus: selectedOrderStatus,
      paymentMethod: selectedPaymentMethod,
      paymentStatus: selectedPaymentStatus,
      search: value,
    });
    setSelectedRowKeys([]);
  };

  const handleDateRange = (dateRange) => {
    setDateRange(dateRange);
    setSelectedRows([]);
    setDisplayCount(20);
    setSelectedRowKeys([]);
  };

  const findOrderById = async (value) => {
    const input = (value || orderIdSearch || "").trim();
    if (!input) {
      message.warning(t("enter_order_id"));
      return;
    }
    try {
      setFindingById(true);
      const snap = await firestore.collection(FIRESTORE_ORDERS_TABLE).where("orderId", "==", input).limit(1).get();
      if (!snap.empty) {
        const doc = snap.docs[0];
        const data = { id: doc.id, ...doc.data() };
        showDrawer(data);
      } else {
        message.error(t("order_not_found", { orderId: input }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to find order by ID", err);
      message.error(t("order_not_found", { orderId: input }));
    } finally {
      setFindingById(false);
    }
  };

  return (
    <>
      {contextHolder}
      {isMobile ? (
        <>
          <div style={{ padding: "8px", paddingBottom: "16px", background: "#fff" }}>
            <Input
              value={searchText}
              placeholder={t("search")}
              prefix={<SearchOutlined />}
              onChange={(e) => onSearch(e)}
            />
          </div>
          <div style={{ padding: "8px" }}>
            {filteredList && filteredList.length > 0 ? (
              <>
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  {[...filteredList]
                    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
                    .slice(0, displayCount)
                    .map((order) => (
                      <OrderCard key={order.id} order={order} />
                    ))}
                </Space>
                {displayCount < filteredList.length && (
                  <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Button
                      type="default"
                      onClick={() => setDisplayCount((prev) => Math.min(prev + 20, filteredList.length))}
                      style={{ minWidth: 200 }}
                    >
                      Laad meer ({filteredList.length - displayCount} resterend)
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Card>
                <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>{t("no_data")}</div>
              </Card>
            )}
          </div>
        </>
      ) : (
        <Card>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <Space wrap>
                <Input
                  value={searchText}
                  placeholder={t("search")}
                  prefix={<SearchOutlined />}
                  onChange={(e) => onSearch(e)}
                />
                <Select
                  defaultValue="All"
                  className="w-100"
                  style={{ minWidth: 180 }}
                  onChange={handleShowStatus}
                  placeholder={t("status")}
                >
                  <Option value="All">{t("all")}</Option>
                  {paymentStatusList.map((elm) => (
                    <Option key={elm} value={elm}>
                      {getPaymentStatus(elm)}
                    </Option>
                  ))}
                </Select>
                <Select
                  value={selectedPaymentMethod}
                  className="w-100"
                  style={{ minWidth: 160 }}
                  onChange={handlePaymentMethodFilter}
                  placeholder={t("payment.method")}
                >
                  <Option value="All">{t("all")}</Option>
                  {PAYMENT_METHODS.map((m) => (
                    <Option key={m} value={m}>
                      {m}
                    </Option>
                  ))}
                </Select>
                <Select
                  value={selectedPaymentStatus}
                  className="w-100"
                  style={{ minWidth: 180 }}
                  onChange={handlePaymentStatusFilter}
                  placeholder={t("payment.status")}
                >
                  <Option value="All">{t("all")}</Option>
                  {(selectedPaymentMethod !== "All"
                    ? PAYMENT_STATUSES_BY_METHOD[selectedPaymentMethod] || []
                    : _.uniq([...PAYMENT_STATUSES_BY_METHOD.invoice, ...PAYMENT_STATUSES_BY_METHOD.stripe])
                  ).map((s) => (
                    <Option key={s} value={s}>
                      {s}
                    </Option>
                  ))}
                </Select>
                <Input.Search
                  value={orderIdSearch}
                  onChange={(e) => setOrderIdSearch(e.target.value)}
                  placeholder={t("search_order_by_id")}
                  enterButton={t("search")}
                  loading={findingById}
                  onSearch={findOrderById}
                  style={{ width: "fit-content" }}
                />
              </Space>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Tooltip title={t("pickup_delivery_date")}>
                <Form form={datePickerForm}>
                  <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
                    <RangePicker
                      onChange={(e) => handleDateRange(e)}
                      locale={locale}
                      placeholder={[t("start_date"), t("end_date")]}
                      format={DATE_FORMAT_DD_MM_YYYY}
                    />
                  </Form.Item>
                </Form>
              </Tooltip>
              <Dropdown overlay={printMenu}>
                <Button className="ant-btn-primary">
                  <PrinterOutlined /> {t("print")} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown overlay={exportMenu}>
                <Button className="ant-btn-primary">
                  <FileExcelOutlined /> {t("export")} <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          </div>
          <div className="table-responsive">
            <Table
              columns={tableColumns}
              dataSource={filteredList}
              rowKey="id"
              rowSelection={{
                selectedRowKeys: selectedRowKeys,
                type: "checkbox",
                preserveSelectedRowKeys: false,
                ...rowSelection,
              }}
              scroll={{ x: 1300 }}
              loading={!isLoaded(orders)}
            />
          </div>
          <div style={{ display: "none" }}>
            {isPrinting && (
              <>
                <Barcode ref={barcodeRef} data={getBarcodeData(getFilteredOrders(null), products)} />
              </>
            )}
          </div>
        </Card>
      )}
      <OrderDrawer order={order} open={visible} closeDrawer={closeDrawer} />
      {!!orderMaxDaysAgoQuery && !isMobile && (
        <div>
          <p>{t("orders_last_period_explanation")}</p>
          <Button onClick={() => dispatch(setOrdersMaxDaysAgo(null))}>{t("retrieve_all_orders")}</Button>
        </div>
      )}
    </>
  );
};

const MobileOrderCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 4px;

  .ant-card-body {
    padding: 12px;
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: scale(0.98);
  }
`;

export default Orders;
